import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { WppMessage, ReplyContext } from "@/hooks/useWhatsAppChat";
import MessageActions from "./MessageActions";
import ReactionPicker from "./ReactionPicker";

export type GroupPosition = "single" | "first" | "middle" | "last";

function checkIsBase64(str: string | undefined | null): boolean {
  if (!str) return false;
  const sample = str.slice(0, 150).replace(/\s/g, "");
  return /^[A-Za-z0-9+/=\/]{40,}/.test(sample);
}

interface MessageBubbleProps {
  message: WppMessage;
  isGroup: boolean;
  contactName: string;
  positionInGroup: GroupPosition;
  onReply?: (ctx: ReplyContext) => void;
  onReact?: (messageId: string, reaction: string) => void;
  onDownloadMedia?: (messageId: string) => Promise<string | null>;
  onAiReply?: (message: WppMessage) => Promise<boolean>;
  aiReplyLoading?: boolean;
  onScrollToMessage?: (targetId: string) => void;
}

function formatMessageTime(timestamp: number): string {
  if (!timestamp) return "";
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
}

function formatAudioDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function hashColor(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

function renderTextWithLinks(text: string) {
  if (!text) return "";
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (/^https?:\/\//i.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline break-all underline"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

function extractFirstUrl(text: string): string | null {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/i;
  const match = text.match(urlRegex);
  return match ? match[0] : null;
}

function LinkPreview({ url }: { url: string }) {
  const [metadata, setMetadata] = useState<{ title?: string; description?: string; domain?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    
    let domainName = "";
    try {
      domainName = new URL(url).hostname;
    } catch {
      domainName = url;
    }

    fetch(`https://api.microlink.io?url=${encodeURIComponent(url)}`)
      .then((res) => res.json())
      .then((json) => {
        if (active && json.status === "success" && json.data) {
          setMetadata({
            title: json.data.title,
            description: json.data.description,
            domain: domainName
          });
        } else if (active) {
          setMetadata({ domain: domainName });
        }
      })
      .catch(() => {
        if (active) setMetadata({ domain: domainName });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [url]);

  if (loading) {
    return (
      <div className="p-3 bg-black/5 dark:bg-black/20 border-b border-foreground-100/5 animate-pulse flex flex-col gap-1.5 text-start w-full min-w-[240px] max-w-[340px]">
        <div className="h-3.5 bg-zinc-200 dark:bg-zinc-700 rounded w-3/4"></div>
        <div className="h-3 bg-zinc-150 dark:bg-zinc-800 rounded w-5/6"></div>
        <div className="h-2.5 bg-zinc-150 dark:bg-zinc-800 rounded w-1/4"></div>
      </div>
    );
  }

  if (!metadata || (!metadata.title && !metadata.description)) {
    return null;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 bg-black/5 dark:bg-black/20 hover:bg-black/10 dark:hover:bg-black/25 border-b border-foreground-100/5 transition-colors text-start w-full min-w-[240px] max-w-[340px]"
      onClick={(e) => e.stopPropagation()}
    >
      {metadata.title && (
        <h4 className="text-xs font-bold text-foreground-900 line-clamp-2 leading-snug mb-1">
          {metadata.title}
        </h4>
      )}
      {metadata.description && (
        <p className="text-[11px] text-foreground-500 dark:text-foreground-400 line-clamp-3 leading-normal mb-1.5">
          {metadata.description}
        </p>
      )}
      {metadata.domain && (
        <span className="text-[10px] text-foreground-400/80 dark:text-foreground-500 font-medium">
          {metadata.domain}
        </span>
      )}
    </a>
  );
}



function getMessagePreviewText(
  type: string,
  body: string,
  t: (k: string) => string,
  caption?: string,
  mediaData?: Record<string, unknown>,
): string {
  const isBase64 = checkIsBase64(body);
  const displayText = caption || (!isBase64 ? body : "");

  switch (type) {
    case "image":
      return displayText || t("msg.photo");
    case "video":
      return displayText || t("msg.video");
    case "audio":
    case "ptt":
      return t("msg.voiceMessage");
    case "document": {
      const filename = (mediaData?.filename as string) || "";
      return filename || t("msg.document");
    }
    case "location":
      return displayText || t("msg.location");
    case "sticker":
      return t("msg.sticker");
    case "contact":
    case "vcard":
      return t("msg.contact");
    case "link":
      return displayText || t("msg.link");
    case "gp2_revoke":
    case "revoked":
      return t("msg.deleted");
    default:
      return displayText || body;
  }
}

function getMessageTypeIcon(type: string): string {
  switch (type) {
    case "image": return "ri-image-line";
    case "video": return "ri-video-line";
    case "audio": return "ri-music-line";
    case "ptt": return "ri-mic-line";
    case "document": return "ri-file-3-line";
    case "location": return "ri-map-pin-line";
    case "sticker": return "ri-sticky-note-line";
    case "contact":
    case "vcard": return "ri-user-line";
    case "link": return "ri-link";
    case "gp2_revoke":
    case "revoked": return "ri-delete-bin-line";
    default: return "ri-chat-1-line";
  }
}

export default function MessageBubble({
  message,
  isGroup,
  contactName,
  positionInGroup,
  onReply,
  onReact,
  onDownloadMedia,
  onAiReply,
  aiReplyLoading = false,
  onScrollToMessage,
}: MessageBubbleProps) {
  const { t } = useTranslation("dashboard");
  const [showActions, setShowActions] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [actionsPos, setActionsPos] = useState({ top: 0, left: 0 });
  const bubbleRef = useRef<HTMLDivElement>(null);

  // --- Media download states ---
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const loadedImageRef = useRef<string | null>(null);

  // Video states
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const loadedVideoRef = useRef<string | null>(null);

  // Sticker states
  const [stickerUrl, setStickerUrl] = useState<string | null>(null);
  const [stickerLoading, setStickerLoading] = useState(false);
  const [stickerError, setStickerError] = useState(false);
  const loadedStickerRef = useRef<string | null>(null);

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioWaveform, setAudioWaveform] = useState<number[]>([]);
  const [audioSpeed, setAudioSpeed] = useState<number>(1);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  const isOutgoing = message.fromMe;
  const isDeleted = message.type === "gp2_revoke" || message.type === "revoked";
  const showTime = (positionInGroup === "single" || positionInGroup === "last") && !isDeleted;
  const showSender = isGroup && !isOutgoing && (positionInGroup === "single" || positionInGroup === "first");

  const senderName = showSender
    ? message.notifyName || message.sender?.pushname || message.sender?.name || contactName
    : null;

  // --- Download image on mount/change ---
  useEffect(() => {
    if (message.type !== "image" || !onDownloadMedia) return;

    // Skip if already loaded for this message
    if (loadedImageRef.current === message.id) return;
    loadedImageRef.current = message.id;

    // Download full media if we don't have the direct base64 string
    if (!message.mediaData?.base64) {
      setImageLoading(true);
      setImageError(false);
      onDownloadMedia(message.id)
        .then((url) => {
          if (url) {
            setImageUrl(url);
          } else {
            const bodyIsBase64 = checkIsBase64(message.body);
            if (!bodyIsBase64) {
              setImageError(true);
            }
          }
          setImageLoading(false);
        })
        .catch(() => {
          const bodyIsBase64 = checkIsBase64(message.body);
          if (!bodyIsBase64) {
            setImageError(true);
          }
          setImageLoading(false);
        });
    }
  }, [message.id, message.type, message.mediaData?.base64, message.body, onDownloadMedia]);

  // --- Download video on mount/change ---
  useEffect(() => {
    if (message.type !== "video" || !onDownloadMedia) return;
    if (loadedVideoRef.current === message.id) return;
    loadedVideoRef.current = message.id;

    if (!message.mediaData?.base64) {
      setVideoLoading(true);
      setVideoError(false);
      onDownloadMedia(message.id)
        .then((url) => {
          if (url) {
            setVideoUrl(url);
          } else {
            setVideoError(true);
          }
          setVideoLoading(false);
        })
        .catch(() => {
          setVideoError(true);
          setVideoLoading(false);
        });
    }
  }, [message.id, message.type, message.mediaData?.base64, onDownloadMedia]);

  // --- Download sticker on mount/change ---
  useEffect(() => {
    if (message.type !== "sticker" || !onDownloadMedia) return;
    if (loadedStickerRef.current === message.id) return;
    loadedStickerRef.current = message.id;

    if (!message.mediaData?.base64) {
      setStickerLoading(true);
      setStickerError(false);
      onDownloadMedia(message.id)
        .then((url) => {
          if (url) {
            setStickerUrl(url);
          } else {
            setStickerError(true);
          }
          setStickerLoading(false);
        })
        .catch(() => {
          setStickerError(true);
          setStickerLoading(false);
        });
    }
  }, [message.id, message.type, message.mediaData?.base64, onDownloadMedia]);

  // --- Reset media state when message changes ---
  useEffect(() => {
    if (loadedImageRef.current !== message.id) {
      loadedImageRef.current = null;
      setImageUrl(null);
      setImageLoading(false);
      setImageError(false);
    }
    if (loadedVideoRef.current !== message.id) {
      loadedVideoRef.current = null;
      setVideoUrl(null);
      setVideoLoading(false);
      setVideoError(false);
    }
    if (loadedStickerRef.current !== message.id) {
      loadedStickerRef.current = null;
      setStickerUrl(null);
      setStickerLoading(false);
      setStickerError(false);
    }
  }, [message.id]);

  // --- Generate audio waveform on audioUrl change ---
  useEffect(() => {
    if (!audioUrl) {
      setAudioWaveform([]);
      return;
    }

    let isMounted = true;
    const generateWaveform = async () => {
      try {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        const audioCtx = new AudioContextClass();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

        if (!isMounted) {
          audioCtx.close();
          return;
        }

        const channelData = audioBuffer.getChannelData(0); // first channel
        const numBars = 16;
        const blockSize = Math.floor(channelData.length / numBars);
        const peaks: number[] = [];

        for (let i = 0; i < numBars; i++) {
          let max = 0;
          const start = i * blockSize;
          for (let j = 0; j < blockSize; j++) {
            const val = Math.abs(channelData[start + j]);
            if (val > max) max = val;
          }
          peaks.push(max);
        }

        const maxPeak = Math.max(...peaks) || 1;
        const normalized = peaks.map((p) => Math.max(0.1, p / maxPeak));

        if (isMounted) {
          setAudioWaveform(normalized);
        }
        await audioCtx.close();
      } catch (err) {
        console.warn("Failed to generate audio waveform, using fallback:", err);
        if (isMounted) {
          // Generate a nice fallback wave using sine + slight randomness so it looks like a real audio clip
          const fallback = Array.from({ length: 16 }, (_, i) =>
            Math.max(0.15, Math.sin((i / 16) * Math.PI) * 0.8 + Math.random() * 0.15)
          );
          setAudioWaveform(fallback);
        }
      }
    };

    generateWaveform();

    return () => {
      isMounted = false;
    };
  }, [audioUrl]);

  // --- Revoke image blob URL when it changes or unmounts ---
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  // --- Revoke video blob URL when it changes or unmounts ---
  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  // --- Revoke sticker blob URL when it changes or unmounts ---
  useEffect(() => {
    return () => {
      if (stickerUrl) {
        URL.revokeObjectURL(stickerUrl);
      }
    };
  }, [stickerUrl]);

  // --- Revoke audio blob URL when it changes or unmounts ---
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // --- Cleanup audio element on unmount ---
  useEffect(() => {
    const audioEl = audioElRef.current;
    return () => {
      if (audioEl) {
        audioEl.pause();
        audioEl.src = "";
      }
    };
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isDeleted) return;
      e.preventDefault();
      const rect = bubbleRef.current?.getBoundingClientRect();
      if (rect) {
        const top = Math.min(e.clientY, window.innerHeight - 120);
        const left = isOutgoing
          ? Math.min(e.clientX - 140, window.innerWidth - 160)
          : Math.max(e.clientX, 10);
        setActionsPos({ top, left });
      }
      setShowActions(true);
    },
    [isOutgoing],
  );

  const handleReply = useCallback(() => {
    if (onReply && bubbleRef.current) {
      onReply({
        messageId: message.id,
        body: getMessagePreviewText(
          message.type,
          message.body,
          t,
          message.caption,
          message.mediaData,
        ),
        type: message.type,
        senderName: isOutgoing ? t("msg.you") : senderName || contactName,
      });
    }
  }, [onReply, message, isOutgoing, senderName, contactName, t]);

  const handleCopy = useCallback(() => {
    const textToCopy = message.body || message.caption || (message.mediaData?.filename as string) || "";
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy).catch((err) => {
        console.error("Failed to copy text: ", err);
      });
    }
  }, [message.body, message.caption, message.mediaData?.filename]);

  const handleReactClick = useCallback(() => {
    const rect = bubbleRef.current?.getBoundingClientRect();
    if (rect) {
      const top = Math.min(rect.top, window.innerHeight - 80);
      const left = isOutgoing
        ? Math.max(rect.right - 220, 10)
        : Math.max(rect.left - 10, 10);
      setActionsPos({ top, left });
    }
    setShowActions(false);
    setShowReactionPicker(true);
  }, [isOutgoing]);

  const handleAiReply = useCallback(async () => {
    if (!onAiReply) return;
    setShowActions(false);
    onAiReply(message);
  }, [onAiReply, message]);

  const handleReaction = useCallback(
    (reaction: string) => {
      if (onReact) onReact(message.id, reaction);
    },
    [onReact, message.id],
  );

  const handleScrollToMessage = useCallback((quotedId?: string, quotedMsgId?: string) => {
    const targetId = quotedId || quotedMsgId;
    if (!targetId) return;

    // 1. Try exact match first
    let el = document.getElementById(`msg-${targetId}`);

    // 2. Try the other ID if we have both
    if (!el && quotedId && quotedMsgId && quotedId !== quotedMsgId) {
      el = document.getElementById(`msg-${quotedMsgId}`);
    }

    // 3. Fallback: extract the unique message token (the alphanumeric segment between JIDs)
    if (!el) {
      const parts = targetId.split("_");
      // The unique message token is the segment that doesn't look like a JID (@c.us/@g.us) and isn't "true"/"false"
      const idTokens = parts.filter((p) => !p.includes("@") && p !== "true" && p !== "false" && p.length >= 8);
      if (idTokens.length > 0) {
        el = document.querySelector(`[id*="msg-"][id*="${idTokens[0]}"]`) as HTMLElement | null;
      }
    }

    // 4. Fallback: try quotedMsgId partial
    if (!el && quotedMsgId && quotedId && quotedId !== quotedMsgId) {
      const parts = quotedMsgId.split("_");
      const idTokens = parts.filter((p) => !p.includes("@") && p !== "true" && p !== "false" && p.length >= 8);
      if (idTokens.length > 0) {
        el = document.querySelector(`[id*="msg-"][id*="${idTokens[0]}"]`) as HTMLElement | null;
      }
    }

    if (el) {
      // Use the parent component's scroll handler if provided (better precision)
      if (onScrollToMessage) {
        onScrollToMessage(targetId);
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Highlight animation
      el.classList.add("bg-amber-100/30", "dark:bg-amber-900/20", "scale-[1.02]", "ring-2", "ring-amber-400/30");
      setTimeout(() => {
        el.classList.remove("bg-amber-100/30", "dark:bg-amber-900/20", "scale-[1.02]", "ring-2", "ring-amber-400/30");
      }, 1500);
    } else {
      console.warn("[ScrollToMessage] Message element not found. Tried IDs:", { quotedId, quotedMsgId, targetId });
    }
  }, [onScrollToMessage]);

  // --- Audio playback handler ---
  const handleAudioPlay = useCallback(async () => {
    // If currently playing, pause
    if (audioPlaying && audioElRef.current) {
      audioElRef.current.pause();
      setAudioPlaying(false);
      return;
    }

    // Resume if paused and already loaded
    if (audioUrl && audioElRef.current) {
      audioElRef.current.currentTime = audioCurrentTime;
      audioElRef.current.play();
      setAudioPlaying(true);
      return;
    }

    // Download audio
    if (!onDownloadMedia) return;
    setAudioLoading(true);
    try {
      const url = await onDownloadMedia(message.id);
      if (!url) {
        setAudioLoading(false);
        return;
      }
      setAudioUrl(url);

      const audio = new Audio(url);
      audio.playbackRate = audioSpeed;
      audioElRef.current = audio;

      audio.addEventListener("loadedmetadata", () => {
        setAudioDuration(audio.duration || 0);
      });

      audio.addEventListener("timeupdate", () => {
        setAudioCurrentTime(audio.currentTime);
      });

      audio.addEventListener("ended", () => {
        setAudioPlaying(false);
        setAudioCurrentTime(0);
      });

      audio.addEventListener("error", () => {
        setAudioPlaying(false);
        setAudioLoading(false);
      });

      await audio.play();
      setAudioPlaying(true);
      setAudioLoading(false);
    } catch {
      setAudioLoading(false);
    }
  }, [audioPlaying, audioUrl, audioCurrentTime, onDownloadMedia, message.id, audioSpeed]);

  const toggleAudioSpeed = useCallback(() => {
    const nextSpeed = audioSpeed === 1 ? 1.5 : audioSpeed === 1.5 ? 2 : 1;
    setAudioSpeed(nextSpeed);
    if (audioElRef.current) {
      audioElRef.current.playbackRate = nextSpeed;
    }
  }, [audioSpeed]);

  // --- WhatsApp-style border radius per position & direction ---
  let radiusClass = "";

  if (isDeleted) {
    radiusClass = "rounded-lg";
  } else if (isOutgoing) {
    switch (positionInGroup) {
      case "single":
        radiusClass = "rounded-lg";
        break;
      case "first":
        radiusClass = "rounded-t-lg rounded-bl-lg rounded-br-[4px]";
        break;
      case "middle":
        radiusClass = "rounded-l-lg rounded-r-[4px]";
        break;
      case "last":
        radiusClass = "rounded-b-lg rounded-l-lg rounded-tr-[4px]";
        break;
    }
  } else {
    switch (positionInGroup) {
      case "single":
        radiusClass = "rounded-lg";
        break;
      case "first":
        radiusClass = "rounded-t-lg rounded-br-lg rounded-bl-[4px]";
        break;
      case "middle":
        radiusClass = "rounded-r-lg rounded-l-[4px]";
        break;
      case "last":
        radiusClass = "rounded-b-lg rounded-r-lg rounded-tl-[4px]";
        break;
    }
  }

  // --- Spacing: same-group messages get minimal gap, different groups get normal gap ---
  const spacingClass =
    positionInGroup === "first" || positionInGroup === "single" ? "mt-2.5" : "mt-0.5";

  const bubbleBg = isDeleted
    ? "bg-background-100/50 text-foreground-400"
    : isOutgoing
      ? "bg-accent-100/80 text-foreground-900"
      : "bg-background-200/80 text-foreground-900";

  const renderContent = () => {
    switch (message.type) {
      case "e2e_notification":
        return (
          <div className="flex items-start gap-1.5 text-[11px] text-foreground-500 py-0.5 max-w-[280px]">
            <i className="ri-lock-line text-[11px] text-foreground-400"></i>
            <span>Messages and calls are end-to-end encrypted. No one outside of this chat can read or listen to them.</span>
          </div>
        );

      case "notification_template":
        return (
          <div className="flex items-start gap-1.5 text-[11px] text-foreground-500 py-0.5 max-w-[280px]">
            <i className="ri-notification-line text-[11px] text-foreground-400"></i>
            <span>{message.body || "System Notification"}</span>
          </div>
        );

      case "ciphertext":
        return (
          <div className="flex items-start gap-1.5 text-[11px] text-foreground-400 italic py-0.5 max-w-[280px]">
            <i className="ri-time-line text-[11px]"></i>
            <span>Waiting for this message. This may take a while.</span>
          </div>
        );

      case "image": {

        const rawBase64 = message.mediaData?.base64 as string | undefined;
        const bodyIsBase64 = checkIsBase64(message.body);
        let imgSrc: string | null = null;

        if (imageUrl) {
          // Downloaded full-res image (preferred for received images)
          imgSrc = imageUrl;
        } else if (rawBase64) {
          // Direct base64 (for sent/temp images)
          if (rawBase64.startsWith("data:")) {
            imgSrc = rawBase64;
          } else {
            const clean = rawBase64.replace(/\s+/g, "");
            const mime = message.mediaData?.mimetype as string || "image/jpeg";
            imgSrc = `data:${mime};base64,${clean}`;
          }
        } else if (bodyIsBase64 && !message.mediaData?.base64) {
          // Fallback: body thumbnail while downloadMedia loads
          const clean = message.body.replace(/\s+/g, "");
          const mime = message.mediaData?.mimetype as string || "image/jpeg";
          imgSrc = `data:${mime};base64,${clean}`;
        }

        // Prefer explicit caption field if populated (e.g. from server payload)
        // Otherwise fallback to body if it's not a base64 thumbnail or placeholder string
        const hasCaption = !!(
          message.caption ||
          (message.body &&
            !bodyIsBase64 &&
            message.body !== "📷 Image" &&
            !(message.mediaData?.base64 && message.body === message.mediaData?.base64))
        );
        const captionText = message.caption || message.body;

        return (
          <div className="max-w-[340px]">
            {imgSrc && !imageError ? (
              <img
                src={imgSrc}
                alt="Sent image"
                className={`w-full object-cover cursor-pointer ${radiusClass} ${hasCaption ? "rounded-b-none" : ""}`}
                onClick={() => {
                  if (!imgSrc) return;
                  const newTab = window.open();
                  if (newTab) {
                    newTab.document.write(`
                      <html>
                        <head>
                          <title>Image Preview</title>
                          <style>
                            body { margin: 0; background: #0b141a; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
                            img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                          </style>
                        </head>
                        <body>
                          <img src="${imgSrc}" />
                        </body>
                      </html>
                    `);
                    newTab.document.close();
                  }
                }}
                onError={() => setImageError(true)}
              />
            ) : (
              <div
                className={`w-[240px] h-[180px] flex items-center justify-center ${radiusClass} bg-background-200/50`}
              >
                {imageLoading ? (
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-foreground-400 text-xl"></i>
                    <span className="text-[11px] text-foreground-400">{t("msg.loadingImage")}</span>
                  </div>
                ) : imageError ? (
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-image-line text-foreground-400 text-2xl"></i>
                    <span className="text-[11px] text-foreground-400">{t("msg.imageUnavailable")}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-image-line text-foreground-400 text-2xl"></i>
                    <span className="text-[11px] text-foreground-400">{t("msg.image")}</span>
                  </div>
                )}
              </div>
            )}
            {hasCaption && (
              <p className="text-sm px-2 pt-1.5 pb-1 leading-relaxed break-words max-w-[340px]">
                {captionText}
              </p>
            )}
          </div>
        );
      }

      case "audio":
      case "ptt": {
        const displayDuration = audioDuration > 0
          ? formatAudioDuration(audioPlaying ? audioDuration - audioCurrentTime : audioDuration)
          : "0:00";
        const progress = audioDuration > 0 ? audioCurrentTime / audioDuration : 0;

        return (
          <div className="flex items-center gap-2 px-3 py-2 min-w-[240px]">
            <button
              onClick={handleAudioPlay}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 cursor-pointer flex-shrink-0 hover:bg-secondary-200 transition-colors"
            >
              {audioLoading ? (
                <i className="ri-loader-4-line animate-spin text-sm"></i>
              ) : audioPlaying ? (
                <i className="ri-pause-fill text-base"></i>
              ) : (
                <i className="ri-play-fill text-base"></i>
              )}
            </button>
            <div className="flex items-center gap-1 flex-1 min-w-[80px] h-6">
              {Array.from({ length: 16 }).map((_, i) => {
                const activeBar = progress > i / 16;
                const barHeight = audioWaveform[i] ? audioWaveform[i] * 100 : 30;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-full transition-colors duration-200 ${
                      audioPlaying
                        ? activeBar
                          ? "bg-secondary-400"
                          : "bg-secondary-200/60"
                        : "bg-secondary-300/60"
                    }`}
                    style={{
                      height: `${Math.max(15, barHeight)}%`,
                    }}
                  />
                );
              })}
            </div>
            <span className="text-[10px] text-foreground-400 flex-shrink-0 min-w-[28px] text-right">
              {displayDuration}
            </span>
            {audioUrl && (
              <button
                onClick={toggleAudioSpeed}
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-secondary-100/80 hover:bg-secondary-200 text-secondary-600 cursor-pointer select-none transition-colors ml-1.5 flex-shrink-0"
                title="Playback Speed"
              >
                {audioSpeed}x
              </button>
            )}
          </div>
        );
      }

      case "location": {
        const lat = message.mediaData?.lat as string;
        const lng = message.mediaData?.lng as string;
        const hasCoords = !!(lat && lng);
        const embedUrl = hasCoords
          ? `https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`
          : "";

        return (
          <div className="max-w-[300px] w-[260px] rounded-lg overflow-hidden">
            <div className="relative h-[160px] bg-background-200/60">
              {hasCoords ? (
                <>
                  <iframe
                    title="Location"
                    className="w-full h-full border-0"
                    loading="lazy"
                    src={embedUrl}
                    allowFullScreen
                  />
                  {/* Subtle overlay at bottom — tap to open full map */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black/40 to-transparent flex items-end justify-center pb-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://maps.google.com/?q=${lat},${lng}`, "_blank");
                    }}
                  >
                    <span className="text-[10px] text-white/90 flex items-center gap-1">
                      <i className="ri-external-link-line text-[10px]"></i>
                      {t("msg.openInMaps")}
                    </span>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-map-pin-line text-2xl text-secondary-500"></i>
                    <p className="text-[10px] text-foreground-500 mt-1">{t("msg.tapToOpenMap")}</p>
                  </div>
                </div>
              )}
            </div>
            {message.body && message.body !== "📍" && (
              <p className="text-sm px-2 pt-1.5 pb-1 leading-relaxed break-words bg-background-100/50">
                {message.body}
              </p>
            )}
          </div>
        );
      }

      case "document":
        return (
          <div className="flex items-center gap-3 px-3 py-2.5 min-w-[200px]">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 flex-shrink-0">
              <i className="ri-file-3-line text-lg text-foreground-500"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-900 truncate">
                {(message.mediaData?.filename as string) || t("msg.file")}
              </p>
              <p className="text-[10px] text-foreground-400">{t("msg.documentType")}</p>
            </div>
            <button
              className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 cursor-pointer flex-shrink-0"
              onClick={async () => {
                const rawBase64 = message.mediaData?.base64 as string | undefined;
                if (rawBase64) {
                  const link = document.createElement("a");
                  if (rawBase64.startsWith("data:")) {
                    link.href = rawBase64;
                  } else {
                    const clean = rawBase64.replace(/\s+/g, "");
                    link.href = `data:application/octet-stream;base64,${clean}`;
                  }
                  link.download = (message.mediaData?.filename as string) || "file";
                  link.click();
                } else if (onDownloadMedia) {
                  const url = await onDownloadMedia(message.id);
                  if (url) {
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = (message.mediaData?.filename as string) || "file";
                    link.click();
                  }
                }
              }}
            >
              <i className="ri-download-line text-xs"></i>
            </button>
          </div>
        );

      case "link": {
        const linkUrl = (message.mediaData?.url as string) || (message.mediaData?.canonicalUrl as string) || message.body || "";
        const title = (message.mediaData?.title as string) || "";
        const description = (message.mediaData?.description as string) || "";
        let domain = "";
        try {
          if (linkUrl) {
            domain = new URL(linkUrl).hostname;
          }
        } catch {
          domain = linkUrl;
        }

        return (
          <div className="flex flex-col max-w-[340px] w-full rounded-xl overflow-hidden text-start">
            {/* Link Preview Card */}
            {(title || description) && (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 bg-black/5 dark:bg-black/20 hover:bg-black/10 dark:hover:bg-black/25 border-b border-foreground-100/5 transition-colors text-start"
                onClick={(e) => e.stopPropagation()}
              >
                {title && (
                  <h4 className="text-xs font-bold text-foreground-900 line-clamp-2 leading-snug mb-1">
                    {title}
                  </h4>
                )}
                {description && (
                  <p className="text-[11px] text-foreground-500 dark:text-foreground-400 line-clamp-3 leading-normal mb-1.5">
                    {description}
                  </p>
                )}
                {domain && (
                  <span className="text-[10px] text-foreground-450 dark:text-foreground-500 font-medium">
                    {domain}
                  </span>
                )}
              </a>
            )}
            
            {/* Original message body (underlined clickable link) */}
            <div className="px-3 py-2 text-start select-text">
              {renderTextWithLinks(message.body || linkUrl)}
            </div>
          </div>
        );
      }

      case "call_log": {
        const bodyText = (message.body || "").toLowerCase();
        const isMissed = bodyText.includes("missed");
        const isVideo = bodyText.includes("video");

        return (
          <div className={`flex items-center gap-2 px-3 py-2.5 min-w-[180px] max-w-[340px]`}>
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                isMissed
                  ? "bg-red-50 text-red-500"
                  : "bg-secondary-100 text-secondary-600"
              }`}
            >
              <i
                className={`text-base ${isVideo ? "ri-video-line" : "ri-phone-line"} ${
                  isMissed ? "" : ""
                }`}
              ></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium truncate ${isMissed ? "text-red-500" : "text-foreground-900"}`}>
                {message.body || (isVideo ? t("msg.videoCall") : t("msg.voiceCall"))}
              </p>
              {isMissed && (
                <p className="text-[10px] text-foreground-400">{t("msg.tapToCallBack")}</p>
              )}
            </div>
            {message.mediaData?.duration && (
              <span className="text-[10px] text-foreground-400 flex-shrink-0 whitespace-nowrap">
                {formatAudioDuration((message.mediaData.duration as number) || 0)}
              </span>
            )}
          </div>
        );
      }

      case "call": {
        const bodyText = (message.body || "").toLowerCase();
        const isMissed = bodyText.includes("missed");
        const isVideo = bodyText.includes("video");

        return (
          <div className={`flex items-center gap-2 px-3 py-2.5 min-w-[180px] max-w-[340px]`}>
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                isMissed
                  ? "bg-red-50 text-red-500"
                  : "bg-secondary-100 text-secondary-600"
              }`}
            >
              <i className={`text-base ${isVideo ? "ri-video-line" : "ri-phone-line"}`}></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium truncate ${isMissed ? "text-red-500" : "text-foreground-900"}`}>
                {message.body || (isVideo ? t("msg.videoCall") : t("msg.voiceCall"))}
              </p>
            </div>
            {message.mediaData?.duration && (
              <span className="text-[10px] text-foreground-400 flex-shrink-0 whitespace-nowrap">
                {formatAudioDuration((message.mediaData.duration as number) || 0)}
              </span>
            )}
          </div>
        );
      }

      case "gp2_revoke":
      case "revoked":
        return (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-delete-bin-line text-[11px] text-foreground-400"></i>
            </div>
            <span className="text-[11px] text-foreground-400 italic leading-relaxed">
              {t("msg.deleted")}
            </span>
          </div>
        );

      case "video": {
        const rawBase64 = message.mediaData?.base64 as string | undefined;
        const bodyIsBase64 = checkIsBase64(message.body);
        let vidSrc: string | null = null;

        if (videoUrl) {
          vidSrc = videoUrl;
        } else if (rawBase64) {
          if (rawBase64.startsWith("data:")) {
            vidSrc = rawBase64;
          } else {
            const clean = rawBase64.replace(/\s+/g, "");
            const mime = message.mediaData?.mimetype as string || "video/mp4";
            vidSrc = `data:${mime};base64,${clean}`;
          }
        } else if (bodyIsBase64 && !message.mediaData?.base64) {
          const clean = message.body.replace(/\s+/g, "");
          const mime = message.mediaData?.mimetype as string || "video/mp4";
          vidSrc = `data:${mime};base64,${clean}`;
        }

        const hasCaption = !!(
          message.caption ||
          (message.body &&
            !bodyIsBase64 &&
            message.body !== "📹 Video" &&
            !(message.mediaData?.base64 && message.body === message.mediaData?.base64))
        );
        const captionText = message.caption || message.body;

        return (
          <div className="max-w-[340px]">
            {vidSrc && !videoError ? (
              <div className="relative cursor-pointer group" onClick={() => window.open(vidSrc, "_blank")}>
                <video
                  src={vidSrc}
                  className={`w-full object-cover ${radiusClass} ${hasCaption ? "rounded-b-none" : ""}`}
                  preload="metadata"
                  muted
                  onError={() => setVideoError(true)}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center">
                    <i className="ri-play-fill text-foreground-900 text-xl ml-0.5"></i>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`w-[240px] h-[180px] flex items-center justify-center ${radiusClass} bg-background-200/50`}
              >
                {videoLoading ? (
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-foreground-400 text-xl"></i>
                    <span className="text-[11px] text-foreground-400">{t("msg.loadingVideo")}</span>
                  </div>
                ) : videoError ? (
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-video-line text-foreground-400 text-2xl"></i>
                    <span className="text-[11px] text-foreground-400">{t("msg.videoUnavailable")}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-video-line text-foreground-400 text-2xl"></i>
                    <span className="text-[11px] text-foreground-400">{t("msg.video")}</span>
                  </div>
                )}
              </div>
            )}
            {hasCaption && (
              <p className="text-sm px-2 pt-1.5 pb-1 leading-relaxed break-words max-w-[340px]">
                {captionText}
              </p>
            )}
          </div>
        );
      }

      case "sticker": {
        const rawBase64 = message.mediaData?.base64 as string | undefined;
        const bodyIsBase64 = checkIsBase64(message.body);
        let stickerSrc: string | null = null;

        if (stickerUrl) {
          stickerSrc = stickerUrl;
        } else if (rawBase64) {
          if (rawBase64.startsWith("data:")) {
            stickerSrc = rawBase64;
          } else {
            const clean = rawBase64.replace(/\s+/g, "");
            const mime = message.mediaData?.mimetype as string || "image/webp";
            stickerSrc = `data:${mime};base64,${clean}`;
          }
        } else if (bodyIsBase64 && !message.mediaData?.base64) {
          const clean = message.body.replace(/\s+/g, "");
          const mime = message.mediaData?.mimetype as string || "image/webp";
          stickerSrc = `data:${mime};base64,${clean}`;
        }

        return (
          <div className="max-w-[160px] max-h-[160px]">
            {stickerSrc && !stickerError ? (
              <img
                src={stickerSrc}
                alt="Sticker"
                className="w-full h-auto object-contain"
                onError={() => setStickerError(true)}
              />
            ) : (
              <div className="w-[120px] h-[120px] flex items-center justify-center bg-background-200/50 rounded-lg">
                {stickerLoading ? (
                  <i className="ri-loader-4-line animate-spin text-foreground-400 text-xl"></i>
                ) : stickerError ? (
                  <div className="flex flex-col items-center gap-1">
                    <i className="ri-sticky-note-line text-foreground-400 text-xl"></i>
                    <span className="text-[10px] text-foreground-400">{t("msg.sticker")}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <i className="ri-sticky-note-line text-foreground-400 text-xl"></i>
                    <span className="text-[10px] text-foreground-400">{t("msg.sticker")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case "vcard":
      case "contact": {
        const contactName = message.mediaData?.displayName as string
          || message.notifyName
          || "";
        const vcard = message.body || "";
        let displayName = contactName;
        let phoneNumbers: string[] = [];

        // Try to parse vcard data
        if (vcard.startsWith("BEGIN:VCARD")) {
          const fnMatch = vcard.match(/FN:(.+)/);
          if (fnMatch) displayName = displayName || fnMatch[1];
          const telMatches = vcard.matchAll(/TEL[^:]*:(.+)/g);
          for (const m of telMatches) {
            phoneNumbers.push(m[1].trim());
          }
        }

        // Fallback: extract phone from message body if it looks like a number
        if (phoneNumbers.length === 0 && /^\d{8,}$/.test(vcard.trim())) {
          phoneNumbers.push(vcard.trim());
        }

        return (
          <div className="flex items-center gap-3 px-3 py-2 min-w-[200px] max-w-[300px]">
            <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-secondary-500 text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-900 truncate">
                {displayName || t("msg.contact")}
              </p>
              {phoneNumbers.length > 0 && (
                <p className="text-[10px] text-foreground-500 truncate">
                  {phoneNumbers[0]}
                </p>
              )}
              <p className="text-[10px] text-foreground-400 mt-0.5">
                {t("msg.contactCard")}
              </p>
            </div>
          </div>
        );
      }

      case "poll":
      case "pollCreation": {
        const pollName = message.body || (message.mediaData?.pollName as string) || "Poll";
        const options = (message.mediaData?.pollOptions || message.mediaData?.options || []) as Array<{ name: string; localId?: string }>;
        return (
          <div className="p-3 min-w-[240px] max-w-[320px]">
            <div className="flex items-center gap-2 mb-2 border-b border-foreground-100 pb-2">
              <i className="ri-survey-line text-secondary-500 text-lg"></i>
              <span className="font-medium text-sm text-foreground-900">{pollName}</span>
            </div>
            <div className="flex flex-col gap-2">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center justify-between bg-background-100/60 hover:bg-background-100 px-3 py-2 rounded-lg border border-background-200/40 text-xs transition-colors">
                  <span className="text-foreground-800">{opt.name || (opt as any)}</span>
                  <div className="w-4 h-4 rounded-full border border-secondary-400 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-transparent"></div>
                  </div>
                </div>
              ))}
              {options.length === 0 && (
                <p className="text-[11px] text-foreground-400 italic">No options defined</p>
              )}
            </div>
            <p className="text-[9px] text-foreground-400 mt-2 text-right">🗳️ WhatsApp Poll</p>
          </div>
        );
      }

      case "multi_vcard": {
        const contactsCount = message.mediaData?.contactsCount as number || 2;
        return (
          <div className="flex items-center gap-3 px-3 py-2.5 min-w-[200px] max-w-[300px]">
            <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
              <div className="relative">
                <i className="ri-user-line text-secondary-500 text-lg"></i>
                <span className="absolute -bottom-1 -right-1 bg-secondary-500 text-[8px] text-white w-4 h-4 rounded-full flex items-center justify-center font-bold">
                  +{contactsCount - 1}
                </span>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-900 truncate">
                {message.body || `${contactsCount} Contacts`}
              </p>
              <p className="text-[10px] text-foreground-400 mt-0.5">
                Shared contacts card
              </p>
            </div>
          </div>
        );
      }

      case "interactive":
      case "buttons":
      case "list":
      case "template": {
        const title = message.body || message.caption || (message.mediaData?.body as string) || (message.mediaData?.text as string) || (message.mediaData?.title as string) || "";
        const buttons = (message.mediaData?.buttons || message.mediaData?.buttonsResponse || message.mediaData?.rows || message.mediaData?.listRows || []) as Array<{ text?: string; title?: string; id?: string }>;
        
        // If it's completely empty (no title, no buttons), render a fallback or description
        if (!title && buttons.length === 0) {
          return (
            <div className="p-3 min-w-[200px] max-w-[320px] flex items-center gap-2 text-foreground-500 italic text-xs">
              <i className="ri-information-line text-sm text-foreground-400"></i>
              <span>Interactive/Template Message</span>
            </div>
          );
        }

        return (
          <div className="p-3 min-w-[220px] max-w-[320px]">
            {title && <p className="text-sm leading-relaxed mb-2 text-start" dir="auto">{title}</p>}
            {buttons.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-2">
                {buttons.map((btn, i) => (
                  <div key={i} className="w-full text-center bg-secondary-50 hover:bg-secondary-100 text-secondary-700 text-xs py-2 px-3 rounded-lg border border-secondary-200/50 font-medium transition-colors">
                    {btn.text || btn.title || (btn as any)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case "product": {
        const prodName = message.mediaData?.title as string || message.body || "Product";
        const prodPrice = message.mediaData?.priceAmount1000 ? (message.mediaData.priceAmount1000 as number / 1000) : null;
        const prodCurrency = message.mediaData?.currencyCode as string || "USD";
        const prodDesc = message.mediaData?.description as string || "";
        return (
          <div className="max-w-[260px] w-full rounded-lg overflow-hidden border border-background-200 bg-background-50">
            <div className="relative h-[160px] bg-background-200/40 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-4xl text-foreground-300 animate-pulse"></i>
            </div>
            <div className="p-2.5">
              <h4 className="font-semibold text-xs text-foreground-900 truncate">{prodName}</h4>
              {prodPrice && (
                <p className="text-xs text-secondary-600 font-bold mt-0.5">
                  {prodPrice.toFixed(2)} {prodCurrency}
                </p>
              )}
              {prodDesc && (
                <p className="text-[10px] text-foreground-500 mt-1 line-clamp-2 leading-normal">
                  {prodDesc}
                </p>
              )}
            </div>
          </div>
        );
      }

      case "order": {
        const orderId = message.mediaData?.orderId as string || "";
        const itemCount = message.mediaData?.itemCount as number || 1;
        return (
          <div className="flex items-center gap-3 px-3 py-2.5 min-w-[220px] max-w-[300px]">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 text-accent-600">
              <i className="ri-receipt-line text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground-900">
                Order Received
              </p>
              {orderId && (
                <p className="text-[10px] text-foreground-500 truncate">
                  ID: {orderId}
                </p>
              )}
              <p className="text-[10px] text-accent-600 font-medium mt-0.5">
                {itemCount} item{itemCount > 1 ? "s" : ""}
              </p>
            </div>
          </div>
        );
      }

      default: {
        const firstUrl = extractFirstUrl(message.body);
        if (firstUrl) {
          return (
            <div className="flex flex-col max-w-[340px] w-full text-start">
              <LinkPreview url={firstUrl} />
              <span className="block text-start select-text px-3 py-2 text-sm leading-relaxed break-words" dir="auto">
                {renderTextWithLinks(message.body)}
              </span>
            </div>
          );
        }
        return (
          <span className={`${showTime ? "" : "me-0"} block text-start select-text`} dir="auto">
            {renderTextWithLinks(message.body)}
          </span>
        );
      }
    }
  };

  const hasUrl = extractFirstUrl(message.body) !== null;
  const isMediaType = [
    "image", "video", "sticker", "audio", "ptt", "location", "document", "link",
    "poll", "pollCreation", "multi_vcard", "interactive", "buttons", "list", "template", "product", "order"
  ].includes(message.type) || (message.type === "chat" && hasUrl);

  return (
    <>
      <div className={`flex ${spacingClass} ${isOutgoing ? "justify-end" : "justify-start"}`}>
        <div className={`max-w-[75%] sm:max-w-[65%] md:max-w-[500px]`}>
          {senderName && (
            <span
              className="text-[11px] font-medium block mb-0.5 px-1"
              style={{
                color: `oklch(0.5 0.14 ${hashColor(senderName)})`,
              }}
            >
              {senderName}
            </span>
          )}
          <div
            ref={bubbleRef}
            onContextMenu={handleContextMenu}
            className={`relative select-text ${
              isDeleted ? "" : "cursor-default"
            } ${
              isMediaType ? "" : `px-3 py-1.5 text-sm leading-relaxed break-words w-fit max-w-full ${radiusClass} ${bubbleBg}`
            }`}
          >
            {isMediaType ? (
              <div className={`overflow-hidden ${radiusClass} ${bubbleBg}`}>
                {message.quotedMsg && !isDeleted && (
                  <div 
                    onClick={() => handleScrollToMessage(message.quotedMsg?.id, message.quotedMsgId)}
                    className="mx-2 mt-2 mb-1 border-l-[3px] border-secondary-400/60 pl-2 py-0.5 bg-background-50/50 rounded-r cursor-pointer hover:bg-background-200/50 transition-colors"
                  >
                    <p className="text-[10px] text-secondary-500 font-medium">
                      {message.quotedMsg.fromMe
                        ? t("msg.you")
                        : message.quotedMsg.senderName || senderName || contactName}
                    </p>
                    <p className="text-[11px] text-foreground-500 truncate max-w-[200px] flex items-center gap-1">
                      <i className={`${getMessageTypeIcon(message.quotedMsg.type)} text-[11px] text-foreground-400 flex-shrink-0`}></i>
                      <span className="truncate">
                        {getMessagePreviewText(
                          message.quotedMsg.type,
                          message.quotedMsg.body,
                          t,
                          message.quotedMsg.caption,
                          message.quotedMsg.mediaData,
                        )}
                      </span>
                    </p>
                  </div>
                )}
                {renderContent()}
                {showTime && (
                  <div className="flex items-center justify-end gap-1 px-2 pb-1.5">
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                      {formatMessageTime(message.timestamp || message.t)}
                    </span>
                    {isOutgoing && (
                      <i
                        className={`text-[10px] ${
                          message.ack && message.ack >= 2
                            ? "ri-check-double-line text-foreground-500"
                            : "ri-check-line text-foreground-400"
                        }`}
                      ></i>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                {message.quotedMsg && !isDeleted && (
                  <div 
                    onClick={() => handleScrollToMessage(message.quotedMsg?.id, message.quotedMsgId)}
                    className="mb-1.5 border-l-[3px] border-secondary-400/60 pl-2 py-0.5 bg-background-50/50 rounded-r cursor-pointer hover:bg-background-200/50 transition-colors"
                  >
                    <p className="text-[10px] text-secondary-500 font-medium">
                      {message.quotedMsg.fromMe
                        ? t("msg.you")
                        : message.quotedMsg.senderName || senderName || contactName}
                    </p>
                    <p className="text-[11px] text-foreground-500 truncate max-w-[200px] flex items-center gap-1">
                      <i className={`${getMessageTypeIcon(message.quotedMsg.type)} text-[11px] text-foreground-400 flex-shrink-0`}></i>
                      <span className="truncate">
                        {getMessagePreviewText(
                          message.quotedMsg.type,
                          message.quotedMsg.body,
                          t,
                          message.quotedMsg.caption,
                          message.quotedMsg.mediaData,
                        )}
                      </span>
                    </p>
                  </div>
                )}
                {renderContent()}
                {showTime && (
                  <span className="inline-flex items-center gap-1 ml-2 float-right mt-1 text-[10px] text-foreground-400 whitespace-nowrap">
                    {formatMessageTime(message.timestamp || message.t)}
                    {isOutgoing && (
                      <i
                        className={`text-[10px] ${
                          message.ack && message.ack >= 2
                            ? "ri-check-double-line text-foreground-500"
                            : "ri-check-line text-foreground-400"
                        }`}
                      ></i>
                    )}
                  </span>
                )}
              </>
            )}

            {/* Reaction badge */}
            {message.reactionText && !isDeleted && (
              <div className={`absolute -bottom-3 ${isOutgoing ? "right-1" : "left-1"}`}>
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-background-50 border border-background-200/60 text-xs shadow-sm">
                  {message.reactionText}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Context menu */}
      <MessageActions
        isOpen={showActions}
        onClose={() => setShowActions(false)}
        onReply={handleReply}
        onReact={handleReactClick}
        isOutgoing={isOutgoing}
        position={actionsPos}
        onAiReply={handleAiReply}
        aiReplyLoading={aiReplyLoading}
        onCopy={handleCopy}
      />

      {/* Reaction picker */}
      <ReactionPicker
        isOpen={showReactionPicker}
        onClose={() => setShowReactionPicker(false)}
        onReact={handleReaction}
        position={actionsPos}
      />
    </>
  );
}